import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  BrandLiveShape,
  BrandLiveSlot,
  SaveBrandLiveSlotRequest,
  createBrandLiveSlot,
  deleteBrandLiveSlot,
  listBrandLiveSlots,
  rotateBrandLiveSlotKey,
  updateBrandLiveSlot,
} from '../../api/payment/brand-live.api';
import DialogComponent from '../dialog/dialog.component';

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const SHAPES: Array<[BrandLiveShape, string]> = [
  ['Corner', 'Góc màn hình'],
  ['BottomBar', 'Thanh dưới'],
  ['Vertical', 'Dải dọc'],
];
const shapeLabel = (s: BrandLiveShape) => SHAPES.find(([k]) => k === s)?.[1] ?? s;
const ROTATIONS = [20, 30, 60];
const EMPTY: SaveBrandLiveSlotRequest = { name: '', shape: 'Corner', maxAds: 2, rotateSeconds: 30 };

export const overlayLink = (slot: BrandLiveSlot) =>
  `${window.location.origin}/ad-screen/${slot.id}?key=${slot.overlayKey}`;

const BrandLiveSlots: React.FC<{ reloadKey?: number }> = ({ reloadKey = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState<BrandLiveSlot[]>([]);
  const [form, setForm] = useState<{ id: string | null; data: SaveBrandLiveSlotRequest } | null>(null);
  const [saving, setSaving] = useState(false);
  const [rotateTarget, setRotateTarget] = useState<BrandLiveSlot | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BrandLiveSlot | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listBrandLiveSlots()
      .then(setSlots)
      .catch((err) => toast.error(errorMessage(err, 'Không tải được vị trí quảng cáo.')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const copy = async (slot: BrandLiveSlot) => {
    try {
      await navigator.clipboard.writeText(overlayLink(slot));
      toast.success('Đã sao chép link.');
    } catch {
      toast.error('Không sao chép được, hãy chọn và chép link bằng tay.');
    }
  };

  const submit = async () => {
    if (!form) return;
    setSaving(true);
    try {
      if (form.id) await updateBrandLiveSlot(form.id, form.data);
      else await createBrandLiveSlot(form.data);
      toast.success('Đã lưu vị trí.');
      setForm(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Không lưu được vị trí.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmRotate = async () => {
    if (!rotateTarget) return;
    setBusy(true);
    try {
      await rotateBrandLiveSlotKey(rotateTarget.id);
      toast.success('Đã đổi khoá. Hãy dán lại link mới vào OBS.');
      setRotateTarget(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Không đổi được khoá.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteBrandLiveSlot(deleteTarget.id);
      toast.success('Đã xoá vị trí.');
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errorMessage(err, 'Không xoá được vị trí.'));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  const setData = (patch: Partial<SaveBrandLiveSlotRequest>) =>
    setForm((f) => (f ? { ...f, data: { ...f.data, ...patch } } : f));

  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="ui-heading text-[15px] font-bold">Vị trí quảng cáo</div>
          <p className="ui-muted text-[13px]">Mỗi vị trí là một khung hiện trên livestream của bạn. Tối đa 3 vị trí.</p>
        </div>
        <button
          type="button"
          onClick={() => setForm({ id: null, data: EMPTY })}
          className="rounded-lg bg-[#0057FF] px-3 py-2 text-[14px] font-semibold text-white"
        >
          Thêm vị trí
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="animate-spin ui-muted" />
        </div>
      ) : slots.length === 0 ? (
        <p className="ui-muted text-[14px]">Bạn chưa có vị trí nào.</p>
      ) : (
        slots.map((slot) => (
          <div key={slot.id} className="ui-surface-2 flex flex-col gap-2 rounded-xl p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="ui-heading text-[14px] font-semibold">{slot.name}</div>
              <span className="ui-muted text-[13px]">
                {shapeLabel(slot.shape)} · {slot.occupied}/{slot.maxAds} quảng cáo · đổi thẻ mỗi {slot.rotateSeconds} giây
              </span>
            </div>
            <input readOnly value={overlayLink(slot)} onFocus={(e) => e.currentTarget.select()} className="ui-input w-full px-3 py-2 text-[13px]" />
            <div className="flex flex-wrap gap-2 text-[13px] font-semibold">
              <button type="button" onClick={() => copy(slot)} className="rounded-lg border border-[#0057FF] px-3 py-1.5 text-[#0057FF]">
                Sao chép link
              </button>
              <button
                type="button"
                onClick={() =>
                  setForm({
                    id: slot.id,
                    data: { name: slot.name, shape: slot.shape, maxAds: slot.maxAds, rotateSeconds: slot.rotateSeconds },
                  })
                }
                className="ui-text rounded-lg border ui-divider px-3 py-1.5"
              >
                Sửa
              </button>
              <button type="button" onClick={() => setRotateTarget(slot)} className="ui-text rounded-lg border ui-divider px-3 py-1.5">
                Đổi khoá
              </button>
              <button type="button" onClick={() => setDeleteTarget(slot)} className="rounded-lg border border-red-500 px-3 py-1.5 text-red-500">
                Xoá
              </button>
            </div>
          </div>
        ))
      )}

      <div className="ui-surface-2 rounded-xl p-3 text-[13px]">
        <div className="ui-heading mb-1 font-semibold">Cách đưa vị trí lên livestream bằng OBS</div>
        <ol className="ui-text list-decimal space-y-1 pl-5">
          <li>Trong OBS, thêm một nguồn mới loại Browser Source.</li>
          <li>Dán link của vị trí vào ô URL.</li>
          <li>Đặt chiều rộng 1280 và chiều cao 720.</li>
          <li>
            Nếu trong khung thấy dòng "Đang xem ngoài OBS, không tính giờ", hãy bật mục "Đọc trạng thái OBS" của nguồn này.
          </li>
        </ol>
        <p className="ui-muted mt-1">Giữ kín link, ai có link cũng hiện được khung này. Nghi bị lộ thì bấm Đổi khoá.</p>
      </div>

      <DialogComponent visible={!!form} title={form?.id ? 'Sửa vị trí' : 'Thêm vị trí'} onClose={() => !saving && setForm(null)}>
        {form && (
          <div className="flex flex-col gap-3 p-4">
            <label className="flex flex-col gap-1">
              <span className="ui-muted text-[13px]">Tên vị trí (1 đến 40 ký tự)</span>
              <input className="ui-input px-3 py-2 text-[14px]" maxLength={40} value={form.data.name} onChange={(e) => setData({ name: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="ui-muted text-[13px]">Kiểu khung</span>
              <select className="ui-input px-3 py-2 text-[14px]" value={form.data.shape} onChange={(e) => setData({ shape: e.target.value as BrandLiveShape })}>
                {SHAPES.map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="ui-muted text-[13px]">Số quảng cáo tối đa cùng lúc</span>
              <select className="ui-input px-3 py-2 text-[14px]" value={form.data.maxAds} onChange={(e) => setData({ maxAds: Number(e.target.value) })}>
                {[1, 2, 3, 4].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="ui-muted text-[13px]">Đổi thẻ sau mỗi</span>
              <select className="ui-input px-3 py-2 text-[14px]" value={form.data.rotateSeconds} onChange={(e) => setData({ rotateSeconds: Number(e.target.value) })}>
                {ROTATIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} giây
                  </option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" disabled={saving} onClick={() => setForm(null)} className="ui-text rounded-lg border ui-divider px-4 py-2 text-[14px]">
                Huỷ
              </button>
              <button type="button" disabled={saving} onClick={submit} className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60">
                {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        )}
      </DialogComponent>

      <DialogComponent visible={!!rotateTarget} title="Đổi khoá link" onClose={() => !busy && setRotateTarget(null)}>
        <div className="flex flex-col gap-3 p-4">
          <p className="ui-text text-[14px]">Link cũ sẽ ngừng hoạt động ngay. Bạn cần dán link mới vào OBS.</p>
          <div className="flex justify-end gap-2">
            <button type="button" disabled={busy} onClick={() => setRotateTarget(null)} className="ui-text rounded-lg border ui-divider px-4 py-2 text-[14px]">
              Huỷ
            </button>
            <button type="button" disabled={busy} onClick={confirmRotate} className="rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60">
              Đổi khoá
            </button>
          </div>
        </div>
      </DialogComponent>

      <DialogComponent visible={!!deleteTarget} title="Xoá vị trí" onClose={() => !busy && setDeleteTarget(null)}>
        <div className="flex flex-col gap-3 p-4">
          <p className="ui-text text-[14px]">Xoá vị trí "{deleteTarget?.name}"?</p>
          <div className="flex justify-end gap-2">
            <button type="button" disabled={busy} onClick={() => setDeleteTarget(null)} className="ui-text rounded-lg border ui-divider px-4 py-2 text-[14px]">
              Huỷ
            </button>
            <button type="button" disabled={busy} onClick={confirmDelete} className="rounded-lg bg-red-600 px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60">
              Xoá
            </button>
          </div>
        </div>
      </DialogComponent>
    </div>
  );
};

export default BrandLiveSlots;
